import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { providers, utils } from 'ethers'
import { useAccount, useConnectorClient } from 'wagmi'
import { Alert, Box, Container, Stack } from '@mui/material'
import { styled } from '@mui/material/styles'
import AppBar from '../AppBar'
import { Button, Card, Input, TextMax, TextM, TextS, TextLinear, Hr } from '../../components'
import { GetGlobalSigner } from '../../ethers/e'
import { CHAIN_ID, REWARD_CONTROLLER } from '../../chain/v2/network'
import { depositWallet, parseDepositAmount, readWalletBalance } from '../../chain/v2/wallet'

export default function V2Wallet() {
    const { address, chainId, isConnected } = useAccount()
    const { data: client } = useConnectorClient()
    const provider = useMemo(() => {
        if (client) return new providers.Web3Provider({ request: args => client.request(args) }, 'any')
        const connection = GetGlobalSigner()
        return connection?.provider || connection
    }, [client])
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(false)
    const [progress, setProgress] = useState('')
    const [error, setError] = useState('')
    const [amount, setAmount] = useState('')
    const [status, setStatus] = useState('')
    const [success, setSuccess] = useState('')
    const [busy, setBusy] = useState(false)
    const request = useRef(0)
    const activeRefresh = useRef(null)
    const invalidate = useCallback(() => { request.current++ }, [])
    const submitting = useRef(false)
    const refresh = useCallback(async () => {
        const id = ++request.current
        setLoading(true)
        setData(null)
        setProgress('')
        setError('')
        try {
            if (!provider) throw new Error('网络尚未就绪，请稍后刷新')
            const result = await readWalletBalance(provider, address, value => {
                if (id === request.current) setProgress(value)
            })
            if (id === request.current) setData(result)
        } catch (e) {
            if (id === request.current) setError(e.message?.startsWith('请') || e.message?.includes('钱包')
                ? e.message : '余额查询失败，请检查网络后刷新；未显示部分余额。')
        } finally {
            if (id === request.current) setLoading(false)
        }
    }, [provider, address])
    useEffect(() => {
        activeRefresh.current = refresh
        refresh()
        return () => {
            invalidate()
            activeRefresh.current = null
        }
    }, [refresh, chainId, invalidate])

    let amountError = ''
    if (amount && data) {
        try {
            const parsed = parseDepositAmount(amount, data.decimals)
            if (data.accountBalance && parsed.gt(data.accountBalance)) amountError = 'USDT 余额不足'
        } catch (e) { amountError = e.message }
    }
    const deposit = async () => {
        if (submitting.current) return
        submitting.current = true
        setBusy(true)
        setError('')
        setSuccess('')
        try {
            const receipt = await depositWallet(provider.getSigner(address), address, amount, setStatus)
            setAmount('')
            setSuccess(`充值成功，交易哈希：${receipt.transactionHash}`)
            await activeRefresh.current?.()
        } catch (e) {
            setError(e.code === 4001 || e.code === 'ACTION_REJECTED' ? '已取消钱包操作' :
                e.code === 'TRANSACTION_REPLACED' && e.cancelled ? '交易已取消，请刷新余额后重试' :
                e.code ? '充值未完成，请检查余额、网络和合约支持情况后重试。已完成的授权可继续使用。' : e.message)
        } finally {
            setBusy(false)
            setStatus('')
            submitting.current = false
        }
    }
    return <>
        <AppBar />
        <Container maxWidth="sm" sx={{ pb: 4 }}>
            <Stack spacing={2} sx={{ overflowWrap: 'anywhere' }}>
                <TextMax style={{ fontSize: 28 }}>Node 提现钱包</TextMax>
                <Card type="j" spacing={1.2}>
                    <Stack spacing={2}>
                        <Box sx={{ textAlign: 'center' }}>
                            <TextM block>分布式钱包可用余额</TextM>
                            <TextLinear linear="3" style={{ display: 'block', fontSize: 'clamp(20px, 5.5vw, 30px)', margin: '12px 0 4px', fontWeight: 700 }}>
                                {loading ? '查询中…' : data ? utils.formatUnits(data.total, data.decimals) : '—'}
                            </TextLinear>
                            <TextS color="2">USDT</TextS>
                        </Box>
                        <Hr />
                        {loading && progress && <TextS color="2">已查询 {progress} 个钱包</TextS>}
                        {data && <Stack direction="row" justifyContent="space-between">
                            <TextS color="2">有效钱包 <TextM>{data.count}</TextM> 个</TextS>
                            <TextS color="2">区块 {data.blockTag}</TextS>
                        </Stack>}
                        <TextS color="2" style={{ lineHeight: 1.7 }}>统计当前提现索引起的全部有效钱包，包含当前钱包剩余余额；不包含已跳过的钱包或 Controller 中尚未分配的 USDT。</TextS>
                        <WalletButton type="e" onClick={refresh} disabled={loading || busy}>刷新余额</WalletButton>
                    </Stack>
                </Card>
                <Card spacing={1.2}>
                    <Stack spacing={2}>
                        <TextM block>充值 USDT</TextM>
                        <TextS color="2" style={{ lineHeight: 1.7 }}>充值用于补充公共提现资金池，不计入个人投资或奖励。首次充值需先授权 USDT，再确认充值交易。</TextS>
                        <Hr />
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                            <TextS color="2">当前账户余额</TextS>
                            <TextS><TextLinear linear="2">{data?.accountBalance ? utils.formatUnits(data.accountBalance, data.decimals) : '—'}</TextLinear> USDT</TextS>
                        </Stack>
                        <Box>
                            <TextS as="label" htmlFor="wallet-deposit-amount" color="2" style={{ display: 'block', marginBottom: 8 }}>充值数量（USDT）</TextS>
                            <Input after={<TextS color="2" style={{ padding: '0 8px', flexShrink: 0, whiteSpace: 'nowrap' }}>USDT</TextS>}
                                sx={{ minHeight: 48, border: '1px solid #393b44', '&:focus-within': { borderColor: '#dcfc35' }, '& .MuiInputBase-root': { minWidth: 0 } }}
                                input={{ id: 'wallet-deposit-amount', value: amount, disabled: busy, placeholder: '输入充值数量',
                                    inputProps: { inputMode: 'decimal', 'aria-label': '充值数量（USDT）', 'aria-invalid': Boolean(amountError) },
                                    onChange: event => setAmount(event.target.value) }} />
                            {amountError && <TextS role="alert" style={{ display: 'block', color: '#ffb4ab', marginTop: 8 }}>{amountError}</TextS>}
                        </Box>
                        {!isConnected && <WalletNotice severity="info">请先通过顶部按钮连接钱包</WalletNotice>}
                        {isConnected && chainId !== CHAIN_ID && <WalletNotice severity="warning">请切换到 BNB Smart Chain 后充值</WalletNotice>}
                        {error && <WalletNotice severity="error">{error}</WalletNotice>}
                        {success && <WalletNotice severity="success">{success}</WalletNotice>}
                        <WalletButton type="d" onClick={deposit}
                            disabled={busy || loading || !data || !amount.trim() || Boolean(amountError) || !client || !isConnected || chainId !== CHAIN_ID}>
                            {busy ? status || '处理中…' : '充值到提现钱包'}
                        </WalletButton>
                    </Stack>
                </Card>
                <Card spacing={1.2}>
                    <Box component="details" sx={{ '& summary': { cursor: 'pointer', color: '#a3a9b6', fontSize: 12 } }}>
                        <summary>钱包与合约信息 · BNB Smart Chain</summary>
                        <Stack spacing={1.5} sx={{ mt: 2 }}>
                            {data && <TextS color="2">提现索引 {data.withdrawIndex} · 充值索引 {data.depositIndex}</TextS>}
                            <TextS color="2">Controller<TextS block>{REWARD_CONTROLLER}</TextS></TextS>
                            {data && <TextS color="2">USDT 合约<TextS block>{data.tokenAddress}</TextS></TextS>}
                        </Stack>
                    </Box>
                </Card>
            </Stack>
        </Container>
    </>
}

const WalletButton = styled(Button)`
    min-height: 42px;
    font-weight: 700;
    &&.Mui-disabled {
        background: #343741;
        color: #c6cad4;
        opacity: 1;
    }
`

const WalletNotice = styled(Alert)`
    background: #24262f;
    color: #c6cad4;
    border: 1px solid #393b44;
    border-radius: 12px;
    font-size: 12px;
    & .MuiAlert-icon { color: #dcfc35; }
    &.MuiAlert-standardError { color: #ffb4ab; }
    &.MuiAlert-standardError .MuiAlert-icon { color: #ffb4ab; }
    &.MuiAlert-standardWarning .MuiAlert-icon { color: #fddd8c; }
`
